import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chrome } from '@/components/layout/Chrome';
import { api, type ApiError } from '@/lib/api';
import { useApplicantStore } from './applicantStore';

export const VerifyPage = () => {
  const navigate = useNavigate();
  const token = useApplicantStore((s) => s.token);
  const pendingEmail = useApplicantStore((s) => s.pendingEmail);
  const setToken = useApplicantStore((s) => s.setToken);
  const [code, setCode] = useState('');

  useEffect(() => {
    // Only bounce back to signup if we have neither a token nor a pending
    // verification in progress. (setToken clears pendingEmail on success —
    // without the token guard we'd race the onSuccess navigate below.)
    if (!pendingEmail && !token) navigate('/apply/signup', { replace: true });
  }, [pendingEmail, token, navigate]);

  const verify = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ token: string; email: string }>('/applicants/verify', {
        email: pendingEmail,
        code,
      });
      return res.data;
    },
    onSuccess: (data) => {
      setToken(data.token, data.email);
      navigate('/register');
    },
  });

  const resend = useMutation({
    mutationFn: async () => {
      await api.post('/applicants/resend', { email: pendingEmail });
    },
  });

  const serverMessage = (err: unknown) =>
    err instanceof AxiosError
      ? (err.response?.data as ApiError | undefined)?.error?.message
      : undefined;

  if (!pendingEmail) return null;

  return (
    <Chrome>
      <div className="mx-auto max-w-md px-6 py-16">
        <h1 className="mb-2 text-2xl font-black text-navy">Check your email</h1>
        <p className="mb-6 text-sm text-gray-600">
          We sent a 6-digit code to <strong>{pendingEmail}</strong>. Enter it below to continue.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (code.length === 6) verify.mutate();
          }}
          className="space-y-4"
        >
          <div>
            <label className="lbl">Verification Code</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              className="inp tracking-[12px] text-center text-2xl font-bold"
              maxLength={6}
            />
          </div>

          {verify.error && (
            <p className="text-sm text-red-600">
              {serverMessage(verify.error) ?? 'Verification failed'}
            </p>
          )}
          {resend.isSuccess && (
            <p className="text-sm text-green-600">A new code has been sent.</p>
          )}
          {resend.error && (
            <p className="text-sm text-red-600">
              {serverMessage(resend.error) ?? 'Could not resend code'}
            </p>
          )}

          <button
            type="submit"
            disabled={verify.isPending || code.length !== 6}
            className="btn-navy w-full"
          >
            {verify.isPending ? 'Verifying…' : 'Verify & Continue'}
          </button>

          <button
            type="button"
            onClick={() => resend.mutate()}
            disabled={resend.isPending}
            className="block w-full text-center text-[11px] uppercase tracking-wider text-gray-500 hover:text-navy"
          >
            {resend.isPending ? 'Sending…' : "Didn't get it? Resend"}
          </button>
          <button
            type="button"
            onClick={() => navigate('/apply/signup')}
            className="block w-full text-center text-[11px] uppercase tracking-wider text-gray-400 hover:text-navy"
          >
            Use a different email
          </button>
        </form>
      </div>
    </Chrome>
  );
};
